import { useState, useRef, useEffect } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import api, { fileUrl, getDownloadToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import PriorityBadge from "@/components/PriorityBadge";
import {
  Mic, Square, Loader2, Upload, Sparkles, Save, ArrowLeft, CheckCircle2,
  ListChecks, Tag, FileAudio, Mail, Send, Gauge, RefreshCw,
  CalendarClock, BellRing, MailCheck, Clock, X, History, Linkedin, Copy, Check,
  Wallet, Banknote, Target, AlertTriangle, Building2, Users, Plus,
  FileText, TrendingUp, Flag, MapPin,
} from "lucide-react";
import { Download as PdfIcon, AlertTriangle as AlertIcon, Columns as CompareIcon, ArrowDown as ArrowDownIcon, Archive as ArchiveIcon, Flame, Thermometer, Snowflake } from "lucide-react";
import { toast } from "sonner";
import { temperature } from "@/lib/score";
import LiveWaveform from "@/components/LiveWaveform";
import ScoreGauge from "@/components/ScoreGauge";
import { addPending } from "@/lib/outbox";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { useAuth } from "@/context/AuthContext";
import { planAllows } from "@/lib/plan";
import { UpgradeLock } from "@/components/UpgradeLock";
import CallImportButton from "@/components/CallImportButton";

const EMPTY_CONTACT = { firstName: "", lastName: "", company: "", role: "", email: "", phone: "", website: "", linkedin: "", address: "" };

const VERDICT_STYLE = {
  progression: { label: "Progression du deal", box: "border-emerald-500/40 bg-emerald-500/5", badge: "bg-emerald-500/15 text-emerald-600", text: "text-emerald-600" },
  regression: { label: "Régression du deal", box: "border-red-500/40 bg-red-500/5", badge: "bg-red-500/15 text-red-500", text: "text-red-600" },
  stagnation: { label: "Deal stable / en stagnation", box: "border-amber-500/40 bg-amber-500/5", badge: "bg-amber-500/15 text-amber-600", text: "text-amber-600" },
};

const TREND_STYLE = {
  new: { label: "Nouveau", cls: "bg-sky-500/10 text-sky-600 dark:text-sky-400" },
  dropped: { label: "Disparu", cls: "bg-red-500/10 text-red-600 dark:text-red-400" },
  changed: { label: "Modifié", cls: "bg-amber-500/10 text-amber-600 dark:text-amber-400" },
  flat: { label: "Inchangé", cls: "bg-muted text-muted-foreground" },
};

const DETAIL_FIELDS = [
  { key: "budget", label: "Budget", icon: Wallet },
  { key: "pricing", label: "Tarifs évoqués", icon: Banknote },
  { key: "timeline", label: "Échéance", icon: CalendarClock },
  { key: "needs", label: "Besoins", icon: Target },
  { key: "objections", label: "Objections", icon: AlertTriangle },
  { key: "competitors", label: "Concurrents", icon: Building2 },
  { key: "decisionMakers", label: "Décideurs", icon: Users },
];

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}

function Waveform({ active, stream }) {
  return <LiveWaveform stream={stream} active={active} color="bg-rose-500" />;
}

function fmtBudget(v) {
  if (v == null) return "";
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(v % 1_000_000 ? 1 : 0)}M€`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(v % 1_000 ? 1 : 0)}k€`;
  return `${v}€`;
}

function AccountEvolutionChart({ debriefs }) {
  const points = [...debriefs]
    .sort((a, b) => (a.createdAt || "").localeCompare(b.createdAt || ""))
    .map((d, i) => ({
      name: `R${i + 1}`,
      date: (d.createdAt || "").slice(0, 10),
      score: d.conversionScore ?? null,
      budget: d.budgetValue ?? null,
    }));
  const scored = points.filter((p) => p.score != null);
  const hasBudget = points.some((p) => p.budget != null);
  if (scored.length < 2) return null;
  return (
    <div className="rounded-2xl border border-border bg-card p-5 sm:p-6 shadow-sm mt-6" data-testid="account-evolution-chart">
      <h3 className="font-heading font-bold text-lg flex items-center gap-2">
        <TrendingUp className="w-4 h-4 text-sky-500" /> Évolution du compte
        <span className="text-sm font-normal text-muted-foreground">· sur {points.length} rencontre{points.length > 1 ? "s" : ""}</span>
      </h3>
      <p className="text-xs text-muted-foreground mt-1">Tendance du score de conversion{hasBudget ? " et du budget évoqué" : ""} au fil des rencontres.</p>
      <div className="mt-4 h-56 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={points} margin={{ top: 8, right: 8, left: -4, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
            <XAxis dataKey="name" tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
            <YAxis yAxisId="score" domain={[0, 100]} tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} width={40} />
            {hasBudget && (
              <YAxis yAxisId="budget" orientation="right" tickFormatter={fmtBudget} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} width={48} />
            )}
            <Tooltip
              contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))", fontSize: 12 }}
              formatter={(val, key) => (key === "budget" ? [fmtBudget(val), "Budget"] : [`${val}%`, "Score"])}
              labelFormatter={(l, pl) => (pl?.[0]?.payload?.date ? `${l} · ${pl[0].payload.date}` : l)}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Line yAxisId="score" type="monotone" dataKey="score" name="Score conversion" stroke="#6366f1" strokeWidth={2.5} dot={{ r: 4 }} activeDot={{ r: 6 }} connectNulls />
            {hasBudget && (
              <Line yAxisId="budget" type="monotone" dataKey="budget" name="Budget" stroke="#10b981" strokeWidth={2} strokeDasharray="5 4" dot={{ r: 3 }} connectNulls />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default function LeadDetailPage() {
  const { state } = useLocation();
  useEffect(() => { if (state?.focusDebrief) setTimeout(() => document.querySelector("[data-testid=voice-record-toggle-button]")?.scrollIntoView({ behavior: "smooth", block: "center" }), 600); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canArchive = planAllows(user, "archiving");
  const canSummary = planAllows(user, "account_summary");
  const canCompare = planAllows(user, "comparator");

  const [leadId, setLeadId] = useState(id && id !== "new" ? id : null);
  const [eventName, setEventName] = useState(state?.eventName || (state?.context === "MEETING" ? "" : localStorage.getItem("klt_event") || ""));
  const [context, setContext] = useState(state?.context || "SALON");
  const [captureMethod, setCaptureMethod] = useState(state?.captureMethod || "MANUAL");
  const [contact, setContact] = useState({ ...EMPTY_CONTACT, ...(state?.contactInfo || {}) });
  const [ai, setAi] = useState({ tags: [], priorityLevel: "", category: "", conversionScore: null, conversionRationale: "", scoreFactors: [] });
  const [debriefs, setDebriefs] = useState(() => (state?.voiceLead ? [{ id: `voice-${Date.now()}`, createdAt: new Date().toISOString(), label: "Dictée salon", transcript: state.voiceLead.transcript || "", summary: [state.voiceLead.project, state.voiceLead.budget && `Budget : ${state.voiceLead.budget}`, state.voiceLead.timeline && `Échéance : ${state.voiceLead.timeline}`].filter(Boolean), details: { budget: state.voiceLead.budget || "", needs: state.voiceLead.project || "", timeline: state.voiceLead.timeline || "", pricing: "", objections: "", competitors: "", decisionMakers: "" }, actionItems: [] }] : []));

  const [recording, setRecording] = useState(false);
  const streamRef = useRef(null);
  const [elapsed, setElapsed] = useState(0);
  const [processing, setProcessing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailDraft, setEmailDraft] = useState({ subject: "", body: "" });
  const [liOpen, setLiOpen] = useState(false);
  const [liLoading, setLiLoading] = useState(false);
  const [liMsg, setLiMsg] = useState("");
  const [liCopied, setLiCopied] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [rescoring, setRescoring] = useState(false);
  const [seq, setSeq] = useState(null);
  const [account, setAccount] = useState(null);
  const [accountLoading, setAccountLoading] = useState(false);
  const [scoreAlert, setScoreAlert] = useState(null);
  const [diffA, setDiffA] = useState("");
  const [diffB, setDiffB] = useState("");
  const [diffResult, setDiffResult] = useState(null);
  const [diffLoading, setDiffLoading] = useState(false);
  const [archived, setArchived] = useState(null);
  const [archiving, setArchiving] = useState(false);
  const [dlToken, setDlToken] = useState("");
  const [pendingPhoto] = useState(state?.pendingPhotoBlob || null);
  const [pendingOcr] = useState(state?.pendingOcr || false);
  const pendingAudioRef = useRef(null);

  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const dropRef = useRef(null);

  useEffect(() => {
    if (leadId) {
      api.get(`/leads/${leadId}`).then(({ data }) => {
        setContact({ ...EMPTY_CONTACT, ...(data.contactInfo || {}) });
        setEventName(data.eventName || "");
        setContext(data.context || "SALON");
        setCaptureMethod(data.captureMethod || "MANUAL");
        setAi({
          tags: data.aiClassification?.tags || [],
          priorityLevel: data.aiClassification?.priorityLevel || "",
          category: data.aiClassification?.category || "",
          conversionScore: data.aiClassification?.conversionScore ?? null,
          conversionRationale: data.aiClassification?.conversionRationale || "",
          scoreFactors: data.aiClassification?.scoreFactors || [],
        });
        // Migrate legacy single audioNotes into a debrief entry
        let dbs = data.debriefs || [];
        if (!dbs.length && (data.audioNotes?.summary?.length || data.audioNotes?.rawTranscript)) {
          dbs = [{
            id: "legacy", createdAt: data.createdAt, audioUrl: data.audioNotes.audioUrl || "",
            transcript: data.audioNotes.rawTranscript || "", summary: data.audioNotes.summary || [],
            details: {}, actionItems: data.audioNotes.actionItems || [],
          }];
        }
        setDebriefs(dbs);
        setSeq(data.followUpSequence || null);
        setAccount(data.accountSummary || null);
        setScoreAlert(data.scoreAlert || null);
        setArchived(data.crmArchived || null);
      }).catch(() => toast.error("Lead introuvable"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId]);

  // Short-lived download token for audio/PDF URLs (avoids the session JWT in URLs).
  useEffect(() => {
    let active = true;
    const refresh = () => getDownloadToken().then((t) => { if (active) setDlToken(t); }).catch(() => {});
    refresh();
    const iv = setInterval(refresh, 8 * 60 * 1000);
    return () => { active = false; clearInterval(iv); };
  }, []);

  const set = (k) => (e) => setContact({ ...contact, [k]: e.target.value });

  useEffect(() => {
    if (debriefs.length >= 2) {
      setDiffA((prev) => (prev && debriefs.some((d) => d.id === prev) ? prev : debriefs[debriefs.length - 2].id));
      setDiffB((prev) => (prev && debriefs.some((d) => d.id === prev) ? prev : debriefs[debriefs.length - 1].id));
    }
  }, [debriefs]);

  const processAudio = async (blob, filename) => {
    if (!navigator.onLine) {
      pendingAudioRef.current = { blob, filename };
      setDebriefs((prev) => [...prev, {
        id: `off_${Date.now()}`, createdAt: new Date().toISOString(), transcript: "",
        summary: ["🎙️ Débrief vocal enregistré hors ligne — transcription et analyse au retour du réseau"],
        details: {}, actionItems: [], pendingAudio: true,
      }]);
      toast("📴 Hors ligne — débrief vocal enregistré, analyse différée");
      return;
    }
    setProcessing(true);
    try {
      const history = debriefs
        .map((d, i) => `Débrief ${i + 1} (${(d.createdAt || "").slice(0, 10)}): ${(d.summary || []).join(" | ")}`)
        .join("\n");
      const fd = new FormData();
      fd.append("file", blob, filename);
      fd.append("contactInfo", JSON.stringify(contact));
      fd.append("history", history);
      const { data } = await api.post("/audio-debrief", fd, { headers: { "Content-Type": "multipart/form-data" } });
      let debrief = data.debrief;
      if (leadId) {
        try {
          const res = await api.post(`/leads/${leadId}/debriefs`, { debrief: data.debrief, aiClassification: data.aiClassification });
          debrief = res.data.debrief;
          if (res.data.accountSummary) setAccount(res.data.accountSummary);
          setScoreAlert(res.data.scoreAlert || null);
          if (res.data.autoFollowup && res.data.followUpSequence) {
            setSeq(res.data.followUpSequence);
            toast.success("🔥 Lead chaud — relance automatique activée (J+1, J+3, J+7)");
          }
        } catch {
          /* fallback: keep local append, persisted on next save */
        }
      }
      setDebriefs((prev) => [...prev, debrief]);
      setAi({
        tags: data.aiClassification?.tags || [],
        priorityLevel: data.aiClassification?.priorityLevel || "",
        category: data.aiClassification?.category || "",
        conversionScore: data.aiClassification?.conversionScore ?? null,
        conversionRationale: data.aiClassification?.conversionRationale || "",
        scoreFactors: data.aiClassification?.scoreFactors || [],
      });
      toast.success("Débrief ajouté à l'historique ✨");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec de l'analyse du débrief");
    } finally {
      setProcessing(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { channelCount: 1, sampleRate: 16000, echoCancellation: true, noiseSuppression: true },
      });
      streamRef.current = stream;
      const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/mpeg"];
      let mime = "";
      if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported) {
        mime = candidates.find((t) => MediaRecorder.isTypeSupported(t)) || "";
      }
      const opts = { audioBitsPerSecond: 32000 };
      if (mime) opts.mimeType = mime;
      const mr = new MediaRecorder(stream, opts);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        const actualType = mr.mimeType || mime || "audio/webm";
        const ext = actualType.includes("mp4") ? "mp4" : actualType.includes("mpeg") ? "mp3" : actualType.includes("ogg") ? "ogg" : "webm";
        const blob = new Blob(chunksRef.current, { type: actualType });
        if (blob.size < 1200) { toast.error("Enregistrement trop court — parlez un instant puis arrêtez."); return; }
        processAudio(blob, `debrief.${ext}`);
      };
      mr.start();
      mediaRef.current = mr;
      setRecording(true);
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
    } catch (err) {
      toast.error("Micro inaccessible. Autorisez l'accès ou importez un fichier audio.");
    }
  };

  const stopRecording = () => {
    if (mediaRef.current && recording) {
      mediaRef.current.stop();
      setRecording(false);
      clearInterval(timerRef.current);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) processAudio(file, file.name);
  };

  const onFilePick = (e) => {
    const file = e.target.files?.[0];
    if (file) processAudio(file, file.name);
  };

  const buildAi = () => ({
    category: ai.category, priorityLevel: ai.priorityLevel || "MOYENNE", tags: ai.tags,
    conversionScore: ai.conversionScore, conversionRationale: ai.conversionRationale, scoreFactors: ai.scoreFactors,
  });

  const saveOffline = async () => {
    await addPending({
      eventName, context, captureMethod, contactInfo: contact, aiClassification: buildAi(),
      photoBlob: pendingPhoto || undefined, pendingOcr,
      audioBlob: pendingAudioRef.current?.blob, audioName: pendingAudioRef.current?.filename,
    });
    window.dispatchEvent(new Event("klt-outbox-changed"));
    toast.success("📴 Enregistré hors ligne — synchronisation automatique au retour du réseau");
    navigate(leadId ? "/dashboard" : "/", leadId ? undefined : { state: { justCaptured: { contactInfo: contact } } });
  };

  const saveLead = async () => {
    setSaving(true);
    try {
      if (!navigator.onLine) { await saveOffline(); return; }
      const payload = {
        id: leadId || undefined, eventName, context, captureMethod, contactInfo: contact,
        aiClassification: buildAi(), debriefs,
      };
      const isNew = !leadId;
      const { data } = await api.post("/leads", payload);
      setLeadId(data.id);
      if (isNew) { navigate("/", { state: { justCaptured: data } }); return; }
      toast.success("Fiche prospect enregistrée ✅");
      navigate("/dashboard");
    } catch (err) {
      if (err.code === "ERR_NETWORK" || !navigator.onLine) {
        try { await saveOffline(); } catch { toast.error("Échec de l'enregistrement local"); }
      } else {
        { const d = err.response?.data?.detail; toast.error(Array.isArray(d) ? d.map((x) => x.msg).join(" · ") : d || "Échec de l'enregistrement"); }
      }
    } finally {
      setSaving(false);
    }
  };

  const openEmail = async () => {
    if (!leadId) return toast.error("Enregistrez d'abord la fiche prospect.");
    if (!contact.email) return toast.error("Ce prospect n'a pas d'adresse email.");
    setEmailOpen(true);
    setEmailLoading(true);
    try {
      const { data } = await api.post(`/leads/${leadId}/email-draft`);
      setEmailDraft(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec de la génération du brouillon");
      setEmailOpen(false);
    } finally {
      setEmailLoading(false);
    }
  };

  const draftLinkedin = async () => {
    if (!leadId) return toast.error("Enregistrez d'abord la fiche prospect.");
    setLiOpen(true);
    setLiLoading(true);
    setLiCopied(false);
    try {
      const { data } = await api.post(`/leads/${leadId}/linkedin-draft`);
      setLiMsg(data.message || "");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec de la génération du message");
      setLiOpen(false);
    } finally {
      setLiLoading(false);
    }
  };

  const copyLi = async () => {
    try { await navigator.clipboard.writeText(liMsg); setLiCopied(true); toast.success("Message copié ✨"); setTimeout(() => setLiCopied(false), 2000); } catch { toast.error("Copie impossible"); }
  };

  const sendFollowUp = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/leads/${leadId}/send-email`, emailDraft);
      toast.success(`Email envoyé à ${data.to} ✉️`);
      setEmailOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec de l'envoi de l'email");
    } finally {
      setSending(false);
    }
  };

  const rescore = async () => {
    if (!leadId) return toast.error("Enregistrez d'abord la fiche prospect.");
    setRescoring(true);
    try {
      const { data } = await api.post(`/leads/${leadId}/rescore`);
      setAi((p) => ({
        ...p,
        conversionScore: data.conversionScore,
        conversionRationale: data.aiClassification?.conversionRationale || p.conversionRationale,
        scoreFactors: data.aiClassification?.scoreFactors || p.scoreFactors,
      }));
      toast.success("Probabilité de signature recalculée");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec du recalcul");
    } finally {
      setRescoring(false);
    }
  };

  const archiveToCrm = async () => {
    if (!leadId) return toast.error("Enregistrez d'abord la fiche prospect.");
    if (!contact.email) return toast.error("Ce prospect n'a pas d'email (requis pour l'archivage CRM).");
    if (!window.confirm("Archiver ce prospect vers votre CRM ? Le texte (résumés, synthèse, score) reste dans l'appli ; les fichiers audio seront libérés pour alléger le stockage.")) return;
    setArchiving(true);
    try {
      const { data } = await api.post(`/leads/${leadId}/archive-to-crm`);
      setArchived(data.crmArchived);
      setDebriefs((prev) => prev.map((d) => ({ ...d, audioUrl: "", audioBytes: 0 })));
      toast.success(`Archivé vers ${data.crmArchived.provider === "hubspot" ? "HubSpot" : "Salesforce"} ✓ audio libéré`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec de l'archivage CRM");
    } finally {
      setArchiving(false);
    }
  };

  const analyzeDiff = async () => {    if (!leadId) return toast.error("Enregistrez d'abord la fiche prospect.");
    if (!diffA || !diffB) return toast.error("Sélectionnez deux rencontres.");
    if (diffA === diffB) return toast.error("Sélectionnez deux débriefs différents.");
    setDiffLoading(true);
    try {
      const { data } = await api.post(`/leads/${leadId}/compare-debriefs`, { aId: diffA, bId: diffB });
      setDiffResult(data);
      toast.success("Analyse différentielle générée ✨");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec de l'analyse différentielle");
    } finally {
      setDiffLoading(false);
    }
  };

  const generateAccountSummary = async () => {
    if (!leadId) return toast.error("Enregistrez d'abord la fiche prospect.");
    if (!debriefs.length) return toast.error("Ajoutez au moins un débrief à synthétiser.");
    setAccountLoading(true);
    try {
      const { data } = await api.post(`/leads/${leadId}/account-summary`);
      setAccount(data);
      toast.success("Synthèse du compte générée ✨");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec de la génération de la synthèse");
    } finally {
      setAccountLoading(false);
    }
  };

  const enableSeq = async () => {
    if (!leadId) return toast.error("Enregistrez d'abord la fiche prospect.");
    if (!contact.email) return toast.error("Ce prospect n'a pas d'adresse email.");
    try {
      const { data } = await api.post(`/leads/${leadId}/followup/enable`);
      setSeq(data);
      toast.success("Séquence de suivi activée (J+1, J+3, J+7)");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec de l'activation");
    }
  };

  const disableSeq = async () => {
    const { data } = await api.post(`/leads/${leadId}/followup/disable`);
    setSeq(data);
    toast.success("Séquence désactivée");
  };

  const markReplied = async () => {
    const { data } = await api.post(`/leads/${leadId}/mark-replied`);
    setSeq(data);
    toast.success("Prospect marqué comme répondu — séquence stoppée");
  };

  const mmss = `${String(Math.floor(elapsed / 60)).padStart(2, "0")}:${String(elapsed % 60).padStart(2, "0")}`;
  const hasAi = ai.conversionScore != null || ai.tags.length > 0 || debriefs.length > 0;
  const scoreColor = (s) => (s >= 66 ? "#10b981" : s >= 33 ? "#f59e0b" : "#ef4444");
  const methodLabel = { OCR_CARD: "OCR Carte", NFC_KALLITAG: "NFC Kallitag", MANUAL: "Saisie manuelle" }[captureMethod];
  const ordered = [...debriefs].reverse();

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4" data-testid="back-button">
        <ArrowLeft className="w-4 h-4" /> Retour
      </button>

      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-xs px-2.5 py-0.5 rounded-full bg-primary/10 text-primary font-medium">{methodLabel}</span>
            {eventName && <span className="text-xs px-2.5 py-0.5 rounded-full bg-secondary text-secondary-foreground font-medium">{eventName}</span>}
            {debriefs.length > 0 && <span className="text-xs px-2.5 py-0.5 rounded-full bg-sky-500/10 text-sky-600 dark:text-sky-400 font-medium">{debriefs.length} débrief{debriefs.length > 1 ? "s" : ""}</span>}
          </div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight mt-2">Fiche Lead & Débriefs</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={openEmail} variant="outline" data-testid="followup-email-button" className="rounded-xl h-11 px-4 font-semibold">
            <Mail className="w-4 h-4 mr-2" /> Relancer par email
          </Button>
          <Button onClick={draftLinkedin} variant="outline" data-testid="followup-linkedin-button" className="rounded-xl h-11 px-4 font-semibold">
            <Linkedin className="w-4 h-4 mr-2" /> Message LinkedIn
          </Button>
          <Button onClick={saveLead} disabled={saving} data-testid="save-lead-button" className="rounded-xl h-11 px-6 font-semibold">
            {saving ? <Loader2 className="w-4 h-4 klt-spin" /> : <><Save className="w-4 h-4 mr-2" /> Enregistrer</>}
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Left: contact form */}
        <div className="rounded-2xl border border-border bg-card p-5 sm:p-6 shadow-sm" data-testid="lead-contact-form">
          <h3 className="font-heading font-bold text-lg flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-sky-500" /> Coordonnées {captureMethod !== "MANUAL" && <span className="text-xs font-normal text-muted-foreground">(pré-rempli par l'IA)</span>}
          </h3>
          <div className="grid grid-cols-2 gap-3 mt-4">
            <Field label="Prénom" testid="lead-first-name-input" value={contact.firstName} onChange={set("firstName")} />
            <Field label="Nom" testid="lead-last-name-input" value={contact.lastName} onChange={set("lastName")} />
            <div className="col-span-2"><Field label="Entreprise" testid="lead-company-input" value={contact.company} onChange={set("company")} /></div>
            <div className="col-span-2"><Field label="Fonction" testid="lead-role-input" value={contact.role} onChange={set("role")} /></div>
            <div className="col-span-2"><Field label="Email" testid="lead-email-input" value={contact.email} onChange={set("email")} /></div>
            <Field label="Téléphone" testid="lead-phone-input" value={contact.phone} onChange={set("phone")} />
            <Field label="Site web" testid="lead-website-input" value={contact.website} onChange={set("website")} />
            <div className="col-span-2"><Field label="LinkedIn" testid="lead-linkedin-input" value={contact.linkedin} onChange={set("linkedin")} /></div>
            <div className="col-span-2"><Field label="Adresse" testid="lead-address-input" value={contact.address} onChange={set("address")} /></div>
          </div>
          {contact.address && contact.address.trim() && (
            <div className="mt-3 rounded-xl overflow-hidden border border-border" data-testid="lead-address-map">
              <iframe
                title="Carte de l'adresse"
                src={`https://maps.google.com/maps?q=${encodeURIComponent(contact.address)}&z=15&output=embed`}
                className="w-full h-40 border-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(contact.address)}`}
                target="_blank"
                rel="noreferrer"
                data-testid="lead-address-maps-link"
                className="flex items-center justify-center gap-2 py-2.5 text-sm font-semibold text-sky-600 hover:bg-sky-500/5 transition-colors"
              >
                <MapPin className="w-4 h-4" /> Ouvrir dans Google Maps
              </a>
            </div>
          )}
          <div className="mt-3">
            <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Salon</Label>
            <Input value={eventName} onChange={(e) => setEventName(e.target.value)} data-testid="lead-event-input" className="mt-1.5" />
          </div>
        </div>

        {/* Right: recorder + AI score */}
        <div className="space-y-6">
          <div className="rounded-2xl border border-border bg-card p-5 sm:p-6 shadow-sm">
            <h3 className="font-heading font-bold text-lg flex items-center gap-2">
              <FileAudio className="w-4 h-4 text-primary" /> Nouveau débrief vocal
            </h3>
            <p className="text-sm text-muted-foreground mt-1">Chaque enregistrement s'ajoute à l'historique du prospect — idéal pour les rendez-vous successifs.</p>

            <div className={`mt-4 rounded-2xl p-5 transition-colors ${recording ? "bg-rose-500/5 border border-rose-200 dark:border-rose-900" : "bg-secondary/50"}`}>
              <Waveform active={recording} stream={streamRef.current} />
              <div className="flex items-center justify-center gap-4 mt-4">
                <span className={`font-mono text-lg font-semibold ${recording ? "text-rose-500" : "text-muted-foreground"}`}>{mmss}</span>
                <Button
                  onClick={recording ? stopRecording : startRecording}
                  disabled={processing}
                  data-testid="voice-record-toggle-button"
                  className={`rounded-full h-14 w-14 p-0 ${recording ? "bg-rose-500 hover:bg-rose-600" : "bg-primary"}`}
                >
                  {processing ? <Loader2 className="w-6 h-6 klt-spin" /> : recording ? <Square className="w-6 h-6" fill="currentColor" /> : <Mic className="w-6 h-6" />}
                </Button>
                <span className="text-sm text-muted-foreground w-12">{recording ? "REC" : "+ Débrief"}</span>
              </div>
            </div>

            <div
              ref={dropRef}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              onClick={() => document.getElementById("audio-file-input").click()}
              className={`mt-4 rounded-xl border-2 border-dashed p-4 text-center cursor-pointer transition-colors ${dragOver ? "border-primary bg-primary/10" : "border-border hover:border-primary/40"}`}
            >
              <input id="audio-file-input" data-testid="audio-dropzone-input" type="file" accept=".mp3,.m4a,.wav,audio/*" onChange={onFilePick} className="hidden" />
              <Upload className="w-5 h-5 mx-auto text-muted-foreground" />
              <p className="text-sm mt-1.5 font-medium">Glissez un fichier audio Plaud</p>
              <p className="text-xs text-muted-foreground">.mp3 / .m4a / .wav — boîtier externe</p>
            </div>
            {processing && (
              <div className="flex items-center justify-center gap-2 py-4 text-muted-foreground text-sm">
                <Loader2 className="w-4 h-4 klt-spin text-primary" /> Transcription Whisper + analyse GPT-4o…
              </div>
            )}
          </div>

          {/* Conversion probability */}
          <div className="rounded-2xl border border-border bg-card p-5 sm:p-6 shadow-sm" data-testid="ai-summary-container">
            <div className="flex items-center justify-between">
              <h3 className="font-heading font-bold text-lg flex items-center gap-2">
                <Gauge className="w-4 h-4 text-sky-500" /> Probabilité de signature
              </h3>
              <div className="flex items-center gap-2">
                {ai.priorityLevel && <PriorityBadge level={ai.priorityLevel} />}
                {leadId && (
                  <Button onClick={rescore} disabled={rescoring} size="sm" variant="ghost" data-testid="rescore-button" className="h-7 rounded-lg text-xs" title="Recalculer">
                    {rescoring ? <Loader2 className="w-3.5 h-3.5 klt-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                  </Button>
                )}
              </div>
            </div>

            {!hasAi ? (
              <p className="text-sm text-muted-foreground mt-4 py-6 text-center">
                Enregistrez un premier débrief vocal pour estimer la probabilité de signature et bâtir l'historique.
              </p>
            ) : (
              <div className="mt-4 space-y-4 klt-fade-up">
                {ai.conversionScore != null && (
                  <div data-testid="conversion-score" className="rounded-xl border border-border bg-secondary/40 p-4">
                    <div className="flex flex-col sm:flex-row items-center gap-4 mb-1">
                      <ScoreGauge score={ai.conversionScore} />
                      <span className="text-sm text-muted-foreground text-center sm:text-left flex-1">Probabilité estimée que ce prospect signe / achète. Ajoutez des débriefs pour l'affiner.</span>
                    </div>
                    {ai.conversionRationale && (
                      <p className="text-sm text-foreground/80 mt-3 leading-relaxed" data-testid="score-rationale">{ai.conversionRationale}</p>
                    )}
                    {ai.scoreFactors?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-3" data-testid="score-factors">
                        {ai.scoreFactors.map((f, i) => {
                          const positive = f.trim().startsWith("+");
                          return (
                            <span key={i} className={`text-xs px-2 py-1 rounded-lg font-medium ${positive ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" : "bg-rose-500/10 text-rose-600 dark:text-rose-400"}`}>
                              {f}
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
                {ai.tags.length > 0 && (
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1"><Tag className="w-3 h-3" /> Tags métier {ai.category && `· ${ai.category}`}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {ai.tags.map((t, i) => (
                        <span key={i} className="text-xs px-2.5 py-1 rounded-full bg-sky-500/10 text-sky-600 dark:text-sky-400 font-medium">{t}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Follow-up sequence */}
          <div className="rounded-2xl border border-border bg-card p-5 sm:p-6 shadow-sm" data-testid="followup-sequence">
            <div className="flex items-center justify-between">
              <h3 className="font-heading font-bold text-lg flex items-center gap-2">
                <CalendarClock className="w-4 h-4 text-primary" /> Séquence de suivi
              </h3>
              {seq?.enabled && <span className="text-xs px-2.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 font-medium">Active</span>}
              {seq?.repliedAt && <span className="text-xs px-2.5 py-0.5 rounded-full bg-secondary text-muted-foreground font-medium">A répondu</span>}
            </div>
            <p className="text-sm text-muted-foreground mt-1">Emails automatiques : remerciement à J+1, puis relances à J+3 et J+7, tant que le prospect n'a pas répondu.</p>
            {!seq?.enabled && !seq?.repliedAt ? (
              <Button onClick={enableSeq} data-testid="enable-followup-button" className="mt-4 rounded-xl w-full">
                <BellRing className="w-4 h-4 mr-2" /> Activer la séquence (J+1, J+3, J+7)
              </Button>
            ) : (
              <div className="mt-4 space-y-2.5">
                {(seq.steps || []).map((s, i) => (
                  <div key={i} className="flex items-center justify-between rounded-xl border border-border p-3">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="w-8 h-8 rounded-lg bg-primary/10 text-primary text-xs font-bold flex items-center justify-center">J+{s.offsetDays}</span>
                      <span className="text-muted-foreground">{new Date(s.scheduledFor).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" })}</span>
                    </div>
                    <StepStatus status={s.status} />
                  </div>
                ))}
                {seq.enabled && !seq.repliedAt && (
                  <div className="flex gap-2 pt-1">
                    <Button onClick={markReplied} variant="outline" data-testid="mark-replied-button" className="rounded-xl flex-1">
                      <MailCheck className="w-4 h-4 mr-2" /> Marquer répondu
                    </Button>
                    <Button onClick={disableSeq} variant="ghost" data-testid="disable-followup-button" className="rounded-xl text-muted-foreground" title="Désactiver">
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Account global summary (full width) */}
      {scoreAlert?.dropped && (
        <div className="rounded-2xl border border-red-500/40 bg-red-500/5 p-4 sm:p-5 shadow-sm mt-6 flex gap-3" data-testid="score-drop-alert">
          <div className="w-10 h-10 rounded-xl bg-red-500/15 flex items-center justify-center shrink-0">
            <AlertIcon className="w-5 h-5 text-red-500" />
          </div>
          <div>
            <p className="font-semibold text-red-600 flex items-center gap-1.5">
              Score de conversion en baisse <ArrowDownIcon className="w-4 h-4" />
            </p>
            <p className="text-sm text-foreground mt-0.5 leading-relaxed">
              Le score est passé de <b>{scoreAlert.from}</b> à <b>{scoreAlert.to}</b> (<b>{scoreAlert.delta}</b>) sur la dernière rencontre.
              Vérifiez les objections et relancez rapidement ce prospect.
            </p>
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-primary/20 bg-primary/5 p-5 sm:p-6 shadow-sm mt-6" data-testid="account-summary">
        {archived && (
          <div className="mb-4 rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3 flex items-center gap-2.5 text-sm" data-testid="archived-banner">
            <ArchiveIcon className="w-4 h-4 text-emerald-600 shrink-0" />
            <span>Archivé vers <b>{archived.provider === "hubspot" ? "HubSpot" : "Salesforce"}</b> le {fmtDate(archived.at)} · audio libéré, texte conservé.</span>
          </div>
        )}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h3 className="font-heading font-bold text-lg flex items-center gap-2">
            <FileText className="w-4 h-4 text-primary" /> Synthèse globale du compte
            {account?.stage && <span className="text-xs px-2.5 py-0.5 rounded-full bg-primary/15 text-primary font-semibold" data-testid="account-stage">{account.stage}</span>}
          </h3>
          <div className="flex items-center gap-2">
            {account && leadId && (
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/leads/${leadId}/synthesis.pdf?auth=${encodeURIComponent(dlToken)}`}
                target="_blank" rel="noopener noreferrer" data-testid="export-pdf-button"
              >
                <Button size="sm" variant="outline" className="rounded-xl"><PdfIcon className="w-4 h-4 mr-2" /> Exporter PDF</Button>
              </a>
            )}
            {leadId && debriefs.length > 0 && !archived && (canArchive ? (
              <Button onClick={archiveToCrm} disabled={archiving} size="sm" variant="outline" data-testid="archive-crm-button" className="rounded-xl">
                {archiving ? <Loader2 className="w-4 h-4 klt-spin" /> : <><ArchiveIcon className="w-4 h-4 mr-2" /> Archiver vers le CRM</>}
              </Button>
            ) : (
              <UpgradeLock feature="archiving" label="Archivage" />
            ))}
            {leadId && debriefs.length > 0 && (canSummary ? (
              <Button onClick={generateAccountSummary} disabled={accountLoading} size="sm" data-testid="generate-account-summary-button" className="rounded-xl">
                {accountLoading ? <Loader2 className="w-4 h-4 klt-spin" /> : <><Sparkles className="w-4 h-4 mr-2" /> {account ? "Régénérer" : "Générer la synthèse"}</>}
              </Button>
            ) : (
              <UpgradeLock feature="account_summary" label="Synthèse" />
            ))}
          </div>
        </div>

        {!account ? (
          <p className="text-sm text-muted-foreground mt-3">
            {!leadId
              ? "Enregistrez la fiche pour pouvoir générer la synthèse."
              : debriefs.length === 0
              ? "Ajoutez au moins un débrief, puis fusionnez-les en une synthèse unique."
              : "Fusionnez tous les débriefs de ce client en une synthèse unique : état de la relation, évolution dans le temps, points clés consolidés et prochaine étape."}
          </p>
        ) : (
          <div className="mt-4 space-y-5 klt-fade-up">
            {account.headline && <p className="text-base font-medium leading-relaxed" data-testid="account-headline">{account.headline}</p>}

            {account.overview?.length > 0 && (
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2">Vue d'ensemble</p>
                <ul className="space-y-1.5">
                  {account.overview.map((s, i) => (
                    <li key={i} className="flex gap-2 text-sm"><CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" /> {s}</li>
                  ))}
                </ul>
              </div>
            )}

            {account.evolution?.length > 0 && (
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1"><TrendingUp className="w-3 h-3" /> Évolution dans le temps</p>
                <ul className="space-y-1.5">
                  {account.evolution.map((s, i) => (
                    <li key={i} className="flex gap-2 text-sm"><TrendingUp className="w-4 h-4 text-sky-500 mt-0.5 shrink-0" /> {s}</li>
                  ))}
                </ul>
              </div>
            )}

            {DETAIL_FIELDS.filter((f) => (account.keyFacts || {})[f.key]).length > 0 && (
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2">Points clés consolidés</p>
                <div className="grid sm:grid-cols-2 gap-2.5">
                  {DETAIL_FIELDS.filter((f) => (account.keyFacts || {})[f.key]).map((f) => (
                    <div key={f.key} className="rounded-xl bg-card border border-border p-3">
                      <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground flex items-center gap-1.5"><f.icon className="w-3.5 h-3.5" /> {f.label}</p>
                      <p className="text-sm mt-1 leading-relaxed">{account.keyFacts[f.key]}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {account.nextStep && (
              <div className="rounded-xl bg-card border border-primary/30 p-3.5 flex gap-2.5" data-testid="account-next-step">
                <Flag className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Prochaine étape recommandée</p>
                  <p className="text-sm mt-0.5 leading-relaxed">{account.nextStep}</p>
                </div>
              </div>
            )}

            <p className="text-xs text-muted-foreground">Basée sur {account.debriefCount} débrief{account.debriefCount > 1 ? "s" : ""} · générée le {fmtDate(account.generatedAt)}</p>
          </div>
        )}
      </div>

      {/* Account evolution mini-graph */}
      <AccountEvolutionChart debriefs={debriefs} />

      {/* Comparateur d'étapes — meetings side by side + intelligent differential */}
      {debriefs.length >= 2 && (
        <div className="rounded-2xl border border-border bg-card p-5 sm:p-6 shadow-sm mt-6" data-testid="step-comparator">
          <h3 className="font-heading font-bold text-lg flex items-center gap-2"><CompareIcon className="w-4 h-4 text-primary" /> Comparateur différentiel intelligent</h3>
          <p className="text-xs text-muted-foreground mt-1">Comparez deux rencontres et laissez l'IA analyser ce qui a évolué.</p>
          <div className="mt-4 flex gap-3 overflow-x-auto pb-2">
            {debriefs.map((d, i) => {
              const prev = i > 0 ? debriefs[i - 1] : null;
              const sc = d.conversionScore;
              const delta = (sc != null && prev?.conversionScore != null) ? sc - prev.conversionScore : null;
              return (
                <div key={d.id || i} className="min-w-[220px] max-w-[240px] rounded-xl border border-border bg-background p-3.5 shrink-0" data-testid={`comparator-step-${i}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-muted-foreground">Rencontre #{i + 1}</span>
                    <span className="text-[11px] text-muted-foreground">{fmtDate(d.createdAt)}</span>
                  </div>
                  {d.label && <div className="text-sm font-medium mt-0.5 truncate">{d.label}</div>}
                  <div className="mt-2 flex items-end gap-2">
                    {sc != null ? (
                      <span className="text-2xl font-extrabold leading-none" style={{ color: scoreColor(sc) }}>{sc}<span className="text-xs font-medium text-muted-foreground">/100</span></span>
                    ) : <span className="text-sm text-muted-foreground">Score n/a</span>}
                    {delta != null && delta !== 0 && (
                      <span className={`text-xs font-semibold inline-flex items-center ${delta < 0 ? "text-red-500" : "text-emerald-600"}`}>
                        {delta < 0 ? <ArrowDownIcon className="w-3 h-3" /> : <TrendingUp className="w-3 h-3" />}{delta > 0 ? "+" : ""}{delta}
                      </span>
                    )}
                  </div>
                  {d.budgetValue != null && <div className="text-xs mt-2"><span className="text-muted-foreground">Budget : </span><span className="font-medium">{d.budgetValue.toLocaleString("fr-FR")} €</span></div>}
                  {d.details?.timeline && <div className="text-xs mt-1"><span className="text-muted-foreground">Échéance : </span>{d.details.timeline}</div>}
                  {d.details?.objections && <div className="text-xs mt-1"><span className="text-muted-foreground">Objections : </span>{d.details.objections}</div>}
                  {d.summary?.length > 0 && <div className="text-xs mt-2 text-muted-foreground line-clamp-3">{d.summary[0]}</div>}
                </div>
              );
            })}
          </div>

          {/* A/B selection + analyze */}
          <div className="mt-5 rounded-xl border border-border bg-secondary/30 p-4" data-testid="diff-selector">
            <div className="grid sm:grid-cols-[1fr_auto_1fr] gap-3 items-end">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Rencontre A (avant)</Label>
                <Select value={diffA} onValueChange={setDiffA}>
                  <SelectTrigger data-testid="diff-select-a" className="rounded-xl"><SelectValue placeholder="Choisir A" /></SelectTrigger>
                  <SelectContent>
                    {debriefs.map((d, i) => (
                      <SelectItem key={d.id || i} value={d.id} data-testid={`diff-a-opt-${i}`}>Rencontre #{i + 1} · {fmtDate(d.createdAt).split(" à")[0]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="hidden sm:flex items-center justify-center pb-2.5 text-muted-foreground"><CompareIcon className="w-4 h-4" /></div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Rencontre B (après)</Label>
                <Select value={diffB} onValueChange={setDiffB}>
                  <SelectTrigger data-testid="diff-select-b" className="rounded-xl"><SelectValue placeholder="Choisir B" /></SelectTrigger>
                  <SelectContent>
                    {debriefs.map((d, i) => (
                      <SelectItem key={d.id || i} value={d.id} data-testid={`diff-b-opt-${i}`}>Rencontre #{i + 1} · {fmtDate(d.createdAt).split(" à")[0]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {canCompare ? (
              <Button onClick={analyzeDiff} disabled={diffLoading || !leadId} data-testid="analyze-diff-button" className="mt-4 rounded-xl w-full font-semibold">
                {diffLoading ? <Loader2 className="w-4 h-4 klt-spin" /> : <><Sparkles className="w-4 h-4 mr-2" /> Analyser le différentiel</>}
              </Button>
            ) : (
              <div className="mt-4 flex justify-center"><UpgradeLock feature="comparator" label="Analyse différentielle" /></div>
            )}
            {canCompare && !leadId && <p className="text-xs text-muted-foreground mt-2 text-center">Enregistrez d'abord la fiche pour lancer l'analyse.</p>}
          </div>

          {diffResult && (
            <div className="mt-5 klt-fade-up space-y-5" data-testid="diff-result">
              <div className={`rounded-xl border p-4 flex gap-3 ${VERDICT_STYLE[diffResult.verdict].box}`}>
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${VERDICT_STYLE[diffResult.verdict].badge}`}>
                  {diffResult.verdict === "progression" ? <TrendingUp className="w-5 h-5" /> : diffResult.verdict === "regression" ? <ArrowDownIcon className="w-5 h-5" /> : <CompareIcon className="w-5 h-5" />}
                </div>
                <div className="flex-1">
                  <p className={`font-semibold ${VERDICT_STYLE[diffResult.verdict].text}`} data-testid="diff-verdict">{VERDICT_STYLE[diffResult.verdict].label}</p>
                  {diffResult.verdictLabel && <p className="text-sm text-foreground mt-0.5 leading-relaxed">{diffResult.verdictLabel}</p>}
                  {diffResult.scoreDelta && (
                    <div className="mt-2 inline-flex items-center gap-2 text-sm">
                      <span className="text-muted-foreground">Score :</span>
                      <span className="font-semibold" style={{ color: scoreColor(diffResult.scoreDelta.from) }}>{diffResult.scoreDelta.from}</span>
                      <span className="text-muted-foreground">→</span>
                      <span className="font-semibold" style={{ color: scoreColor(diffResult.scoreDelta.to) }}>{diffResult.scoreDelta.to}</span>
                      {diffResult.scoreDelta.delta !== 0 && (
                        <span className={`font-semibold inline-flex items-center ${diffResult.scoreDelta.delta < 0 ? "text-red-500" : "text-emerald-600"}`} data-testid="diff-score-delta">
                          ({diffResult.scoreDelta.delta > 0 ? "+" : ""}{diffResult.scoreDelta.delta})
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                {diffResult.improved?.length > 0 && (
                  <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4" data-testid="diff-improved">
                    <p className="text-xs font-medium uppercase tracking-wider text-emerald-600 dark:text-emerald-400 mb-2 flex items-center gap-1.5"><TrendingUp className="w-3.5 h-3.5" /> Ce qui s'est amélioré</p>
                    <ul className="space-y-1.5">
                      {diffResult.improved.map((s, i) => <li key={i} className="flex gap-2 text-sm"><CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" /> {s}</li>)}
                    </ul>
                  </div>
                )}
                {diffResult.degraded?.length > 0 && (
                  <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-4" data-testid="diff-degraded">
                    <p className="text-xs font-medium uppercase tracking-wider text-red-600 dark:text-red-400 mb-2 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" /> Ce qui s'est dégradé</p>
                    <ul className="space-y-1.5">
                      {diffResult.degraded.map((s, i) => <li key={i} className="flex gap-2 text-sm"><AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" /> {s}</li>)}
                    </ul>
                  </div>
                )}
              </div>

              {diffResult.evolution?.length > 0 && (
                <div data-testid="diff-evolution">
                  <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2">Évolution point par point</p>
                  <div className="space-y-2">
                    {diffResult.evolution.map((e, i) => (
                      <div key={i} className="rounded-xl border border-border bg-background p-3" data-testid={`diff-evolution-${e.field}`}>
                        <div className="flex items-center gap-2 mb-1.5">
                          <span className="text-xs font-semibold">{e.label}</span>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-md font-medium ${TREND_STYLE[e.trend].cls}`}>{TREND_STYLE[e.trend].label}</span>
                        </div>
                        <div className="grid grid-cols-[1fr_auto_1fr] gap-2 items-center text-sm">
                          <span className="text-muted-foreground line-through decoration-muted-foreground/40 break-words">{e.before || "—"}</span>
                          <span className="text-muted-foreground">→</span>
                          <span className="font-medium break-words">{e.after || "—"}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {diffResult.recommendedActions?.length > 0 && (
                <div className="rounded-xl border border-primary/30 bg-primary/5 p-4" data-testid="diff-actions">
                  <p className="text-xs font-medium uppercase tracking-wider text-primary mb-2 flex items-center gap-1.5"><Flag className="w-3.5 h-3.5" /> Actions recommandées</p>
                  <ul className="space-y-1.5">
                    {diffResult.recommendedActions.map((a, i) => (
                      <li key={i} className="flex gap-2 text-sm items-start"><span className="w-5 h-5 rounded-md bg-primary/10 text-primary text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span> {a}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {(() => {
        const kf = (account?.keyFacts) || (ordered[0]?.details) || {};
        const bant = [
          { k: "Budget", v: kf.budget, Icon: Wallet },
          { k: "Autorité", v: kf.decisionMakers, Icon: Users },
          { k: "Besoin", v: kf.needs, Icon: Target },
          { k: "Timing", v: kf.timeline, Icon: CalendarClock },
        ];
        const done = bant.filter((x) => x.v).length;
        const pct = Math.round((done / 4) * 100);
        const pcol = pct >= 75 ? "#10b981" : pct >= 50 ? "#38bdf8" : pct >= 25 ? "#f59e0b" : "#94a3b8";
        return (
          <div className="rounded-2xl border border-border bg-card p-5 sm:p-6 shadow-sm mt-6" data-testid="bant-panel">
            <div className="flex items-center gap-2">
              <h3 className="font-heading font-bold text-lg flex items-center gap-2"><Target className="w-4 h-4 text-primary" /> Qualification BANT</h3>
              <span className="ml-auto text-sm font-extrabold tabular-nums" style={{ color: pcol }} data-testid="bant-completion">{done}/4 · {pct}%</span>
            </div>
            <div className="h-2 rounded-full bg-border overflow-hidden mt-3">
              <div className="h-full rounded-full klt-gauge-bar" style={{ width: `${pct}%`, background: pcol }} />
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
              {bant.map((b) => { const filled = !!b.v; return (
                <div key={b.k} data-testid={`bant-${b.k.toLowerCase()}`} className={`rounded-xl border p-3 transition-all ${filled ? "border-emerald-500/40 bg-emerald-500/5" : "border-dashed border-border bg-secondary/30"}`}>
                  <p className="text-xs font-semibold uppercase tracking-wider flex items-center gap-1.5"><b.Icon className="w-3.5 h-3.5" /> {b.k}</p>
                  <div className="h-1.5 rounded-full bg-border overflow-hidden mt-2">
                    <div className="h-full rounded-full klt-gauge-bar bg-emerald-500" style={{ width: filled ? "100%" : "0%" }} />
                  </div>
                  {filled ? <p className="text-sm mt-2 leading-relaxed">{b.v}</p> : <p className="text-xs mt-2 text-muted-foreground">À qualifier</p>}
                </div>
              ); })}
            </div>
          </div>
        );
      })()}

      {/* Chronological debrief history (full width) */}
      <div className="rounded-2xl border border-border bg-card p-5 sm:p-6 shadow-sm mt-6" data-testid="debrief-history">
        <h3 className="font-heading font-bold text-lg flex items-center gap-2">
          <History className="w-4 h-4 text-sky-500" /> Historique des débriefs
          {debriefs.length > 0 && <span className="text-sm font-normal text-muted-foreground">· {debriefs.length}</span>}
          {leadId && <CallImportButton leadId={leadId} label="Synthèse d'appel" className="ml-auto min-h-8 py-1 px-3 rounded-full bg-[#3B82F6]/15 text-[#60A5FA] text-xs font-semibold inline-flex items-center gap-1.5" />}
        </h3>
        {ordered.length === 0 ? (
          <p className="text-sm text-muted-foreground mt-4 py-6 text-center flex flex-col items-center gap-2">
            <Plus className="w-5 h-5" /> Aucun débrief pour l'instant. Enregistrez-en un ci-dessus.
          </p>
        ) : (
          <div className="mt-5 space-y-4">
            {ordered.map((d, idx) => {
              const num = debriefs.length - idx;
              const details = DETAIL_FIELDS.filter((f) => (d.details || {})[f.key]);
              return (
                <div key={d.id || idx} className="relative pl-6 border-l-2 border-border" data-testid="debrief-entry">
                  <span className="absolute -left-[7px] top-1.5 w-3 h-3 rounded-full bg-primary ring-4 ring-card" />
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-bold">Débrief #{num}</span>
                    <span className="text-xs text-muted-foreground flex items-center gap-1"><Clock className="w-3 h-3" /> {fmtDate(d.createdAt)}</span>
                    {leadId && dlToken && (
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/leads/${leadId}/meeting-report.pdf?auth=${encodeURIComponent(dlToken)}${d.id ? `&debriefId=${encodeURIComponent(d.id)}` : ""}`}
                        target="_blank" rel="noopener noreferrer" className="ml-auto"
                        data-testid={`meeting-report-button-${d.id || num}`}
                      >
                        <Button size="sm" variant="outline" className="h-7 rounded-lg text-xs px-2.5"><PdfIcon className="w-3.5 h-3.5 mr-1.5" /> Compte-rendu client</Button>
                      </a>
                    )}
                  </div>

                  {d.summary?.length > 0 && (
                    <ul className="mt-2.5 space-y-1.5">
                      {d.summary.map((s, i) => (
                        <li key={i} className="flex gap-2 text-sm"><CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" /> {s}</li>
                      ))}
                    </ul>
                  )}

                  {details.length > 0 && (
                    <div className="grid sm:grid-cols-2 gap-2.5 mt-3">
                      {details.map((f) => (
                        <div key={f.key} className="rounded-xl bg-secondary/40 border border-border p-3">
                          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground flex items-center gap-1.5"><f.icon className="w-3.5 h-3.5" /> {f.label}</p>
                          <p className="text-sm mt-1 leading-relaxed">{d.details[f.key]}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  {d.actionItems?.length > 0 && (
                    <div className="mt-3">
                      <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1.5 flex items-center gap-1"><ListChecks className="w-3 h-3" /> Prochaines actions</p>
                      <ul className="space-y-1.5">
                        {d.actionItems.map((a, i) => (
                          <li key={i} className="flex gap-2 text-sm items-start"><span className="w-5 h-5 rounded-md bg-primary/10 text-primary text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span> {a}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {d.audioUrl && (
                    <audio controls src={dlToken ? fileUrl(d.audioUrl, dlToken) : undefined} className="w-full mt-3 h-9" data-testid="debrief-audio-player" />
                  )}
                  {d.transcript && (
                    <details className="text-sm mt-2">
                      <summary className="cursor-pointer text-muted-foreground hover:text-foreground font-medium">Voir la transcription brute</summary>
                      <p className="mt-2 text-muted-foreground leading-relaxed whitespace-pre-wrap">{d.transcript}</p>
                    </details>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent data-testid="email-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Relance par email</DialogTitle>
            <DialogDescription>Brouillon généré par l'IA — modifiez-le puis envoyez à {contact.email}.</DialogDescription>
          </DialogHeader>
          {emailLoading ? (
            <div className="flex flex-col items-center py-10 text-muted-foreground">
              <Loader2 className="w-7 h-7 klt-spin text-primary" />
              <p className="mt-3 text-sm">Rédaction du brouillon…</p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label>Objet</Label>
                <Input data-testid="email-subject-input" value={emailDraft.subject} onChange={(e) => setEmailDraft({ ...emailDraft, subject: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Message</Label>
                <Textarea data-testid="email-body-input" rows={9} value={emailDraft.body} onChange={(e) => setEmailDraft({ ...emailDraft, body: e.target.value })} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailOpen(false)} className="rounded-xl">Annuler</Button>
            <Button onClick={sendFollowUp} disabled={sending || emailLoading} data-testid="send-email-button" className="rounded-xl">
              {sending ? <Loader2 className="w-4 h-4 klt-spin" /> : <><Send className="w-4 h-4 mr-2" /> Envoyer</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={liOpen} onOpenChange={setLiOpen}>
        <DialogContent data-testid="linkedin-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Message LinkedIn</DialogTitle>
            <DialogDescription>Brouillon généré par l'IA — copiez-le et collez-le dans LinkedIn.</DialogDescription>
          </DialogHeader>
          {liLoading ? (
            <div className="flex flex-col items-center py-10 text-muted-foreground">
              <Loader2 className="w-7 h-7 klt-spin text-primary" />
              <p className="mt-3 text-sm">Rédaction du message…</p>
            </div>
          ) : (
            <Textarea data-testid="linkedin-message-input" rows={6} value={liMsg} onChange={(e) => setLiMsg(e.target.value)} />
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setLiOpen(false)} className="rounded-xl">Fermer</Button>
            <Button onClick={copyLi} disabled={liLoading || !liMsg} data-testid="copy-linkedin-button" className="rounded-xl">
              {liCopied ? <><Check className="w-4 h-4 mr-2" /> Copié</> : <><Copy className="w-4 h-4 mr-2" /> Copier</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function LeadTempBadge({ score }) {
  const t = temperature(score);
  if (!t) return null;
  const Icon = t.key === "chaud" ? Flame : t.key === "tiede" ? Thermometer : Snowflake;
  return <span data-testid={`lead-temp-badge-${t.key}`} className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${t.cls}`}><Icon className="w-3.5 h-3.5" />{t.label}</span>;
}

function StepStatus({ status }) {  if (status === "sent")
    return <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Envoyé</span>;
  if (status === "failed")
    return <span className="text-xs font-medium text-rose-600">Échec</span>;
  return <span className="text-xs font-medium text-muted-foreground flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Programmé</span>;
}

function Field({ label, testid, value, onChange }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</Label>
      <Input data-testid={testid} value={value || ""} onChange={onChange} />
    </div>
  );
}
